import React from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import { useMediaQuery } from '@material-ui/core';
import Image from 'next/image';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { marketplaceURL } from 'shared/helpers/urls';
import CitySelector from 'static_pages/components/CitySelector';
import HeroImages from 'static_pages/components/HeroImages';
import Container from 'system/components/Container';

function Hero({
  title,
  subtitle,
  virtualEvent = false,
  cityFromSlug,
  keywords = [],
  term = '',
}) {
  const { t } = useTranslation('static_pages');
  const fromTabletDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const fromDesktopUp = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const keyword = keywords[0] || 'hero';

  return (
    <React.Fragment>
      <MainWrapper>
        <HeroImages keyword={keyword} />

        <HeroContent>
          <Container size="sm">
            {(() => {
              if (title) {
                return <Typography variant="h1">{title}</Typography>;
              }

              if (virtualEvent) {
                return (
                  <Typography variant="h1">
                    {t('virtualEventsHero.title.a')}{' '}
                    <Typography variant="inherit" slab="true" color="secondary">
                      {t('virtualEventsHero.title.b')}
                    </Typography>
                  </Typography>
                );
              }

              return (
                <Typography variant="h1">
                  <Trans
                    t={t}
                    i18nKey="baseHero.title"
                    components={[<span key="span" />]}
                  />
                </Typography>
              );
            })()}
          </Container>
          <Container size="xs">
            <Typography variant="subtitle2" color="textSecondary" mt={4} mb={4}>
              {(() => {
                if (subtitle) return subtitle;
                if (virtualEvent) return t('virtualEventsHero.subtitle');
                return t('baseHero.subtitle');
              })()}
            </Typography>
          </Container>
          <Box
            pl={2}
            pr={2}
            width="100%"
            display="flex"
            justifyContent="center"
          >
            {(() => {
              if (virtualEvent) {
                return (
                  <Button
                    size="lg"
                    rounded
                    as="a"
                    href={marketplaceURL('/catering/virtual_events')}
                  >
                    {t('common:labels.findVirtualEvents')}
                  </Button>
                );
              }

              return (
                <Box width="100%" maxWidth="500px">
                  <CitySelector
                    width="100%"
                    cityFromSlug={cityFromSlug}
                    cateringCategories={keywords}
                    term={term}
                  />
                </Box>
              );
            })()}
          </Box>
        </HeroContent>

        {fromDesktopUp && (
          <CustomersTrustElement>
            <Image
              alt=""
              src="/artifacts/images/homepage/hero-customers-trust-element@4x.webp"
              width={2476}
              height={134}
            />
          </CustomersTrustElement>
        )}
      </MainWrapper>
      {fromTabletDown && (
        <Container>
          <Box pt={2} display="flex" justifyContent="center" mb={2}>
            <Image
              alt=""
              src="/artifacts/images/homepage/hero-customers-trust-element@4x.webp"
              width={619}
              height={34}
            />
          </Box>
        </Container>
      )}
    </React.Fragment>
  );
}

const MainWrapper = styled.div`
  background: ${(props) => props.theme.colors.heroGradient};
  padding: ${(props) => props.theme.spacing(10, 0, 14)};
  position: relative;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.down('sm')} {
    padding: ${(props) => props.theme.spacing(6, 0, 20)};
  }
`;

const HeroContent = styled.header`
  text-align: center;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

export const CustomersTrustElement = styled.div`
  position: absolute;
  bottom: ${(props) => props.theme.spacing(2)}px;
  left: 50%;
  margin-left: ${(props) => props.theme.spacing(1)}px;
  transform: translateX(-50%);
  max-width: 650px;
`;

export default Hero;
